/**
 * English copy, transcribed from "06 - Build Passing - site".
 *
 * The voice, from the brand kit: sentence case, lowercase mono labels, no
 * exclamation marks, no adjectives doing work a fact could do. It says what
 * happens and what it costs.
 *
 * Prices are not here. They live in RATES in lib/constants, because the two
 * languages quote different numbers rather than translations of one number.
 */
import type { Dictionary } from './types';

const en: Dictionary = {
  meta: {
    title: 'Build Passing — software studio in México, working across the US & México',
    description:
      'We build the software your business runs on: web platforms, mobile apps, Odoo and APIs. Fixed scope, fixed price per stage, and you own the code from the first commit.',
  },

  nav: {
    services: 'services',
    products: 'products',
    method: 'method',
    studio: 'studio',
    cta: 'request a proposal',
    menu: 'Menu',
    closeMenu: 'Close menu',
    languageLabel: 'Language',
    skipToContent: 'Skip to content',
  },

  hero: {
    eyebrow: 'Software studio · México · working across the US & México',
    headline: 'We build the software your business runs on. And our own, to prove we can.',
    body: 'Web platforms, mobile apps and Odoo, engineered to a fixed scope. You own the code from the first commit.',
    ctaPrimary: 'Request a proposal',
    ctaSecondary: 'see our products',
    stack: [
      'Python · Django · Flask',
      'Node.js · REST APIs',
      'React · TypeScript',
      'Flutter · iOS & Android',
      'Odoo ERP',
    ],
    productsNote: '3 products in the line',
  },

  services: {
    eyebrow: 'what we take on',
    title: 'Four practices, quoted separately',
    lead: 'We scope them separately and quote them fixed, so you always know what a stage costs before it starts.',
    items: [
      {
        num: '01',
        label: 'web platforms',
        title: 'Systems that run the business',
        body: 'Internal tools, customer portals, dashboards and admin systems. Django, Flask or Node and Express on the back end, React and TypeScript on the front, PostgreSQL underneath. The unglamorous software a company actually depends on.',
      },
      {
        num: '02',
        label: 'mobile',
        title: 'iOS and Android, one codebase',
        body: 'Flutter applications from design through store submission and release management. We ship our own apps this way, so the process is one we run monthly, not occasionally.',
      },
      {
        num: '03',
        label: 'odoo & erp',
        title: 'Odoo that fits how you work',
        body: "Implementation, custom modules and data migration for companies outgrowing spreadsheets, without bending your operation to fit somebody else's default configuration.",
      },
      {
        num: '04',
        label: 'apis & integrations',
        title: 'APIs we build, and APIs we tame',
        body: "We design and build REST APIs in Node or Python: versioned, documented, authenticated. And we integrate the ones you're already stuck with. Webhooks, rate limits, retries and the failure cases nobody writes down, so your CRM, accounting, store and ERP finally agree with each other.",
      },
    ],
  },

  products: {
    eyebrow: 'our product line · one in the App Store, two on the way',
    title: 'We are our own toughest client',
    lead: "Most studios only build on someone else's budget. We fund and ship our own products too, which means our release process, store handling and maintenance discipline are tested on software we can't walk away from.",
    items: [
      {
        name: 'Rickey',
        status: 'in the App Store',
        body: 'A cocktail app that answers one question well: what can you actually make tonight from the bottles already on your shelf?',
        progress: 'released on the App Store · landing page live',
        imageAlt: "Rickey landing page: tonight's menu, from your shelf",
      },
      {
        name: 'TwoCoins',
        status: 'in development',
        body: 'A personal finance manager for people who gave up on spreadsheets: accounts, categories, and an honest answer to where the month went.',
        progress: 'in development · shipping as a PWA at twocoins.app',
        imageAlt: 'TwoCoins landing page: built for how credit works in Mexico',
      },
      {
        name: 'Two Milks',
        status: 'next to ship',
        body: 'Groceries, chores and reminders for the people you actually live with: roommates, couples, families. One list, synced, without arguing about who bought the milk.',
        progress: 'nearing release · App Store next',
        imageAlt: 'Two Milks landing page: one grocery list the whole house shares',
      },
    ],
    visit: 'visit site',
  },

  method: {
    eyebrow: 'how an engagement runs',
    title: 'Four stages, each quoted on its own',
    lead: 'Each stage is quoted and invoiced separately. You can stop after any stage and keep everything produced up to that point.',
    stages: [
      {
        num: '01',
        label: 'discovery',
        title: 'One week, fixed',
        body: 'We learn how the work happens today, then deliver a written scope with a real number in it. Stop here and the document is yours to take anywhere.',
      },
      {
        num: '02',
        label: 'design',
        title: 'Screens before code',
        body: 'Clickable screens you can put in front of a real user before production code exists. The cheapest possible place to change your mind.',
      },
      {
        num: '03',
        label: 'build',
        title: 'Two-week increments',
        body: "Working software on a real URL every two weeks, with a written note of what changed. No status decks, and you always have the engineer's ear.",
      },
      {
        num: '04',
        label: 'handover',
        title: 'Release, documentation, keys',
        body: "Store submission, monitoring, written technical documentation and every account transferred into your name. Retain us for upkeep or don't. You're never locked in.",
      },
    ],
  },

  standards: {
    eyebrow: 'standards',
    title: 'Included in every engagement',
    lead: 'Not upsells. These are the terms we work under, written into the contract.',
    items: [
      {
        title: 'You own the code',
        body: 'Repository, servers, domains and store listings in your accounts and your name, from day one.',
      },
      {
        title: 'Fixed price per stage',
        body: 'Agreed before work starts. If we estimate badly, we absorb it. You never receive a surprise invoice.',
      },
      {
        title: 'Written documentation',
        body: 'Architecture, deployment and operations, written so your next developer can pick it up without calling us.',
      },
      {
        title: 'One working day to reply',
        body: 'During an engagement, every message gets a real answer inside one business day. In writing, in the contract.',
      },
      {
        title: 'Tested before it ships',
        body: 'Automated tests on anything that touches money or data, and a staging environment you can break freely.',
      },
      {
        title: '30 days of warranty',
        body: "Anything we built that doesn't behave as specified gets fixed at no charge for 30 days after release.",
      },
    ],
  },

  stack: {
    eyebrow: 'what we run',
    groups: [
      { label: 'back end', value: 'Python · Django · Flask · Node.js · Express' },
      { label: 'front end', value: 'React · TypeScript' },
      { label: 'mobile', value: 'Flutter · iOS & Android' },
      { label: 'data', value: 'PostgreSQL · migrations · backups' },
      { label: 'erp', value: 'Odoo · custom modules' },
      { label: 'interfaces', value: 'REST APIs · webhooks · third-party integrations' },
    ],
    note: 'Every project ships with staging, automated tests on anything touching money or data, and documentation written for whoever maintains it next.',
  },

  studio: {
    title: 'Small, senior, and staffed per project',
    lead: 'Every engagement is led by a senior engineer with six years of production work in Python, Django, Flask, Node, React, Odoo and Flutter. The person who sets your architecture is on every call you have with us.',
    body: "The studio works under ongoing contract to software companies in the United States and México, building production systems in Python and PostgreSQL, and currently Node.js, Express and React with TypeScript. We can't name those clients, but it is why we talk about US teams, US timezones and US code-review standards from experience rather than ambition.",
    points: [
      {
        title: 'No bench to feed',
        body: 'Specialists join per project from a network we already work with: design, QA, native platform work. You pay for the skills your build needs and nothing else.',
      },
      {
        title: 'No junior assigned quietly',
        body: 'The engineers who scope the work are the engineers who build it.',
      },
      {
        title: 'No handover to strangers',
        body: 'The team that ships is the team you met at kickoff.',
      },
    ],
    facts: [
      { label: 'Founded', value: '2026 · México' },
      { label: 'Languages', value: 'English & Spanish' },
      { label: 'Hours', value: 'CST · US business day' },
    ],
  },

  engagements: {
    title: 'Three ways to work with us',
    items: [
      {
        tag: 'fixed scope',
        name: 'Project',
        body: 'A defined build with a written scope and a fixed price per stage. Best when you know what you need made.',
        terms: '4–16 weeks',
      },
      {
        tag: 'monthly',
        name: 'Retainer',
        body: "A set number of engineering days each month for ongoing development, upkeep and releases. Cancel with 30 days' notice.",
        terms: 'rolling',
      },
      {
        tag: 'embedded',
        name: 'With your team',
        body: 'We join your existing team and standups as senior capacity for a defined period. Useful when you have a team but not enough of one.',
        terms: 'min. 4 weeks',
      },
    ],
  },

  offer: {
    title: '30% off our first three client engagements',
    body: "We're opening the studio's client practice, and we want three reference projects we're proud to publish. In exchange for a case study you approve line by line, the first three engagements are billed at 30% below our standard rate.",
    points: [
      {
        title: 'Full standard terms apply.',
        body: 'Same fixed pricing, same documentation, same warranty. The discount comes off the rate, never off the work.',
      },
      {
        title: 'You approve every published word.',
        body: "If you'd rather not be named, we'll publish the work anonymously or not at all.",
      },
      {
        title: 'Stop at any stage.',
        body: "You're invoiced for delivered work you've seen running, and you keep everything produced.",
      },
    ],
    cta: 'Ask about a founding engagement',
  },

  proposal: {
    eyebrow: 'request a proposal',
    title: 'Tell us what you need built',
    body: "Tell us what you need built, or what's gone wrong with what you have. Every request is read by an engineer, not a form queue, and answered in writing within one business day. If we're not the right studio for it, we'll say so and point you somewhere better.",
    location: 'México · CST',
    fields: {
      name: 'Name',
      company: 'Company',
      optional: 'optional',
      email: 'Work email',
      kind: 'What do you need?',
      budget: 'Budget range',
      deadline: 'Needs to be live by',
      detail: 'What are you building?',
      detailHint: 'The more concrete, the more useful the first reply.',
    },
    kinds: [
      { value: 'web', label: 'web platform' },
      { value: 'mobile', label: 'mobile app' },
      { value: 'odoo', label: 'odoo / erp' },
      { value: 'integrations', label: 'integrations' },
      { value: 'rescue', label: 'rescue a project' },
    ],
    budgets: [
      { value: 'under-10k', label: 'Under $10k' },
      { value: '10k-30k', label: '$10k – $30k' },
      { value: '30k-75k', label: '$30k – $75k' },
      { value: 'over-75k', label: 'Over $75k' },
      { value: 'guidance', label: 'Need guidance' },
    ],
    submit: 'send request',
    sending: 'sending…',
    reply: 'we reply today',
    successTitle: 'Request received',
    successBody:
      "An engineer is reading it. You'll have a written answer within one business day, to the address you gave us.",
    errors: {
      missing_name: 'We need a name to address the reply to.',
      missing_email: 'Please add an email address so we can reply.',
      invalid_email: "That email address doesn't look right. Please check it.",
      email_too_long: 'That email address is too long.',
      missing_detail: 'Tell us a little about what you need built — a sentence or two is plenty.',
      detail_too_long:
        'That is longer than the form accepts. Please shorten it, or email us directly.',
      field_too_long: 'One of those answers is longer than the form accepts.',
      invalid_kind: 'Please choose what kind of work this is.',
      invalid_budget: 'Please choose a budget range, or "Need guidance".',
      rate_limited: 'That is a lot of requests from one place. Please try again in a few minutes.',
      spam_detected:
        'That request looked automated. If you are a person, please email us directly.',
      send_failed: "We couldn't send that just now. Please email us directly and we'll pick it up.",
      server_error: "Something broke on our side. Please email us directly and we'll pick it up.",
    },
    offerNote: 'Founding-engagement pricing is open — mention it and we will apply it.',
  },

  legal: {
    contents: 'Contents',
    lastUpdated: 'Last updated',
    effectiveDate: 'Effective date',
    bindingNotice:
      'This English text is the binding version. The Spanish translation is provided for convenience.',
    questionsTitle: 'Questions',
    questionsBody: 'Write to us and a person will answer.',
  },

  footer: {
    tagline: 'Software studio · México',
    rights: 'All rights reserved.',
    github: 'github',
    linkedin: 'linkedin',
    privacy: 'privacy',
    cookies: 'cookies',
  },

  notFound: {
    eyebrow: 'not found',
    title: 'Nothing at this address',
    body: 'The page you asked for is not here. It may have moved, or the link may have a typo in it.',
    home: 'Back to the front page',
    contact: 'Email us',
    status: 'status · 404 not found',
    cta: 'Back to the front page',
  },
};

export default en;
